import React, { useState } from 'react';
import { Button } from '../ui/button';
import { useLanguage } from '../../contexts/LanguageContext';
import { STEP_IDS } from './usePackageForm';
import StepIndicator from './StepIndicator';
import SummaryBar from './SummaryBar';
import RouteStep from './steps/RouteStep';
import ContactsStep from './steps/ContactsStep';
import ItemsStep from './steps/ItemsStep';
import PaymentStep from './steps/PaymentStep';

const STEP_COMPONENTS = {
  route: RouteStep, contacts: ContactsStep, items: ItemsStep, payment: PaymentStep,
};

// Champs à 16 px sous 640 px : en dessous, iOS Safari zoome automatiquement
// à la mise au point du champ et décale toute la mise en page.
const MOBILE_FIELD_SCALE = '[&_input]:text-base [&_select]:text-base [&_textarea]:text-base '
  + 'sm:[&_input]:text-sm sm:[&_select]:text-sm sm:[&_textarea]:text-sm';

/**
 * Affiche les mêmes sections en parcours guidé (création) ou d'un seul bloc
 * (correction d'un colis, validation d'un brouillon).
 */
const PackageFormShell = ({ layout = 'wizard', variant = 'partner', api, stepProps = {}, footer }) => {
  const { t, language } = useLanguage();
  const [index, setIndex] = useState(0);
  const [maxReached, setMaxReached] = useState(0);
  // Vrai quand une tentative d'avancer a échoué : sans ce rappel, une erreur
  // portant sur un champ non affiché donnerait un bouton « Continuer » inerte.
  const [blocked, setBlocked] = useState(false);

  const steps = STEP_IDS.map((id) => ({ id, label: t(`packageStep_${id}`) }));
  const common = {
    form: api.form,
    errors: api.errors,
    variant,
    totals: api.totals,
    onField: api.setField,
    onItem: api.setItem,
    onItemCount: api.setItemCount,
    onAddItem: api.addItem,
    onRemoveItem: api.removeItem,
    ...stepProps,
  };

  if (layout === 'single') {
    return (
      <div className={`space-y-6 py-4 ${MOBILE_FIELD_SCALE}`}>
        {STEP_IDS.map((id) => {
          const StepComponent = STEP_COMPONENTS[id];
          return <StepComponent key={id} {...common} />;
        })}
        {footer}
      </div>
    );
  }

  const goNext = () => {
    if (!api.validateStep(STEP_IDS[index])) {
      setBlocked(true);
      return;
    }
    setBlocked(false);
    const next = Math.min(index + 1, STEP_IDS.length - 1);
    setIndex(next);
    setMaxReached((m) => Math.max(m, next));
  };

  const StepComponent = STEP_COMPONENTS[STEP_IDS[index]];
  const isLast = index === STEP_IDS.length - 1;

  return (
    <div className={`space-y-4 py-4 ${MOBILE_FIELD_SCALE}`}>
      <div className="sticky top-0 z-10 -mx-4 bg-background px-4 pb-2 pt-1 sm:static sm:mx-0 sm:bg-transparent sm:px-0 sm:pb-0">
        <StepIndicator
          steps={steps}
          currentIndex={index}
          maxReachedIndex={maxReached}
          onSelect={setIndex}
        />
      </div>
      <div className="space-y-0.5">
        <p className="text-xs text-muted-foreground">
          {language === 'en'
            ? `Step ${index + 1} of ${STEP_IDS.length}`
            : `Étape ${index + 1} sur ${STEP_IDS.length}`}
        </p>
        <h2 className="text-lg font-semibold text-foreground">{steps[index].label}</h2>
      </div>
      <StepComponent {...common} />

      {blocked && Object.keys(api.errors).length > 0 && (
        <p className="landing-form-error" role="alert" style={{ margin: 0 }}>
          {language === 'en'
            ? 'Some required fields are missing or invalid:'
            : 'Des champs obligatoires sont manquants ou invalides :'}
          {' '}
          {Object.values(api.errors).join(' · ')}
        </p>
      )}

      <SummaryBar form={api.form} totals={api.totals} variant={variant} />
      {/* Barre d'action collée en bas sur mobile : le bouton « Continuer »
          reste atteignable sans dérouler tout le formulaire. */}
      <div className="sticky bottom-0 -mx-4 flex flex-col-reverse gap-2 border-t border-border bg-background px-4 py-3 sm:static sm:mx-0 sm:flex-row sm:justify-between sm:border-0 sm:bg-transparent sm:p-0">
        <Button
          type="button" variant="outline" className="w-full sm:w-auto"
          disabled={index === 0} onClick={() => setIndex(index - 1)}
        >
          ← {t('back')}
        </Button>
        {isLast ? footer : (
          <Button type="button" className="w-full sm:w-auto" onClick={goNext}>
            {t('continue')} →
          </Button>
        )}
      </div>
    </div>
  );
};

export default PackageFormShell;
